import React, { useState } from 'react';
import styled from 'styled-components';

import {
  Portal,
  Popover,
  PopoverTrigger,
  PopoverHeader,
  PopoverFooter,
  PopoverBody,
  PopoverContent,
  PopoverButtonGroup,
  FormControl,
  FormLabel,
  Divider,
  Button,
} from '@frontend/components';
import {
  SortAscendingIcon,
  SortDescendingIcon,
} from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';
import { theme } from '@frontend/components/src/theme/theme';

import { useTableContext } from '../utils/TableContext';
import constants from '../utils/constants';
import updateConfigs from '../utils/updateConfigs';

const SortButtonsWrapper = styled.span`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const FilterSortButton = styled.button`
  align-items: center;
  background-color: ${(props) => props.theme.colors.page.plateLight};
  border-radius: 0.125rem;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-family: ${(props) => props.theme.fonts.default};
  font-weight: ${(props) => props.theme.fontWeights.medium};
  line-height: ${(props) => props.theme.lineHeights.sm};
  min-width: fit-content;
  padding-left: 0.125rem;
  padding-right: 1rem;
  text-transform: capitalize;
  user-select: none;

  :hover:enabled {
    background-color: ${(props) => props.theme.colors.interaction.surfaceHover};
  }

  :focus {
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.interaction.focus};
    outline: none;
  }

  :disabled {
    background-color: transparent;
    color: ${(props) => props.theme.colors.interaction.disabled};
    cursor: not-allowed;

    .icon {
      color: ${(props) => props.theme.colors.interaction.disabled};
    }
  }

  .icon {
    color: ${(props) => props.theme.colors.feature.secondary};
    margin-right: 0.25rem;
  }

  ${(props) =>
    props.$checked == true &&
    `
    background-color: ${props.theme.colors.interaction.main};
    color: ${props.theme.colors.feature.onInteraction};
    .icon {
      color: ${props.theme.colors.feature.onInteraction};
    }
    :hover:enabled {
      background-color: ${props.theme.colors.interaction.hover};
    }

 `}
`;

FilterSortButton.defaultProps = { theme };

const PopoverSortButtons = ({ value, setValue, sortDirections }) => (
  <SortButtonsWrapper>
    <FilterSortButton
      type="button"
      disabled={!sortDirections.includes(constants.ascendingIdentifier)}
      tabIndex={0}
      $checked={value == constants.ascendingIdentifier}
      onClick={() =>
        setValue(
          value != constants.ascendingIdentifier
            ? constants.ascendingIdentifier
            : null,
        )
      }
    >
      <SortAscendingIcon size={2} className="icon" />
      Ascending
    </FilterSortButton>
    <FilterSortButton
      type="button"
      disabled={!sortDirections.includes(constants.descendingIdentifier)}
      tabIndex={0}
      $checked={value == constants.descendingIdentifier}
      style={{ marginLeft: '0.5rem' }}
      onClick={() =>
        setValue(
          value != constants.descendingIdentifier
            ? constants.descendingIdentifier
            : null,
        )
      }
    >
      <SortDescendingIcon size={2} className="icon" />
      Descending
    </FilterSortButton>
  </SortButtonsWrapper>
);

/**
 * @deprecated use TableV2 and its subcomponents from '@frontend/components' instead.
 */
const FilterPopover = ({
  dataIndex,
  title,
  filterInputComponent,
  formatter,
  empty,
  isOpen,
  onClose,
  onApply,
  onClear,
  children,
}) => {
  const emptyState = empty ?? constants.emptyFilterState;
  const { filterConfigs, setFilterConfigs } = useTableContext();

  const [configStore, setConfigStore] = useState({
    sortState: {
      direction: constants.emptySortDirection,
      priority: constants.emptySortPriority,
    },
    filterState: emptyState,
    sortDirections: [],
  });
  // initial store: a snapshot of the corresponding filterConfig at the time of opening the popover...
  // ...so we can compare to current values and enable or disable the submit button.
  const [initialConfigStore, setInitialConfigStore] = useState({
    sortState: {
      direction: constants.emptySortDirection,
      priority: constants.emptySortPriority,
    },
    filterState: emptyState,
    sortDirections: [],
  });

  return (
    <Popover
      onClose={onClose}
      isOpen={isOpen}
      onOpen={() => {
        // Whenever the popover opens...
        // ...set the config store to one with the given dataIndex found within filterConfigs...
        // ...so when the popover opens, its buttons and inputs are populated with the current filtering.
        // NOTE: all of this is done in case the filters are changed externally by a global clear or column sorting button.
        if (filterConfigs != null) {
          setConfigStore(filterConfigs[dataIndex]);
          // also set the initial store, so we can check if values have changed to enable the submit button.
          setInitialConfigStore(filterConfigs[dataIndex]);
        }
      }}
      autoFocus
      size="sm"
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent width="max-content">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const newConfigs = updateConfigs(
                filterConfigs,
                dataIndex,
                configStore,
              );
              setFilterConfigs(newConfigs);
              setConfigStore(newConfigs[dataIndex]);
              setInitialConfigStore(newConfigs[dataIndex]);
              onApply();
            }}
          >
            <PopoverHeader>Filter: {title}</PopoverHeader>
            <PopoverBody>
              <FormControl mb={1.5}>
                <FormLabel>sort</FormLabel>
                <PopoverSortButtons
                  value={configStore.sortState.direction}
                  setValue={(value) => {
                    // Whenever the popover sort buttons are updated,
                    // mutate the internal config store with an updated sortState.
                    setConfigStore({
                      ...configStore,
                      sortState: {
                        direction: value,
                        priority: configStore.sortState.priority,
                      },
                    });
                  }}
                  sortDirections={configStore.sortDirections}
                />
              </FormControl>
              <Divider />
              <FormControl mt={1} mb={0.5}>
                <FormLabel>filter</FormLabel>
                {React.cloneElement(filterInputComponent, {
                  ...filterInputComponent.props,
                  value: configStore.filterState || undefined,
                  style: filterInputComponent.props.style || {
                    width: '100%',
                  },
                  onChange: (output) => {
                    // This logic ensures this works for react components
                    //... and generic html inputs with event-based outputs.
                    let finalOutput = output;
                    if (output?.target != null) {
                      finalOutput = output.target.value;
                    }
                    if (formatter) {
                      finalOutput = formatter(finalOutput);
                    }

                    setConfigStore({
                      ...configStore,
                      filterState: finalOutput,
                    });
                  },
                })}
              </FormControl>
            </PopoverBody>
            <PopoverFooter>
              <PopoverButtonGroup>
                <Button
                  variant="secondary"
                  onClick={() => {
                    const { sortState } = configStore;
                    const newConfig = {
                      ...configStore,
                      sortState: {
                        direction: constants.emptySortDirection,
                        priority: sortState.priority,
                      },
                      filterState: emptyState,
                    };
                    const newConfigs = updateConfigs(
                      filterConfigs,
                      dataIndex,
                      newConfig,
                    );
                    setFilterConfigs(newConfigs);
                    setConfigStore(newConfigs[dataIndex]);
                    setInitialConfigStore(newConfigs[dataIndex]);
                    onClear();
                  }}
                  isDisabled={
                    configStore.sortState.direction ==
                      constants.emptySortDirection &&
                    configStore.filterState == emptyState
                  }
                >
                  clear
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isDisabled={
                    initialConfigStore.sortState.direction ==
                      configStore.sortState.direction &&
                    initialConfigStore.filterState == configStore.filterState
                  }
                >
                  apply
                </Button>
              </PopoverButtonGroup>
            </PopoverFooter>
          </form>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default FilterPopover;
